import {useFieldControl} from "../control";
import React, {useCallback, useEffect} from "react";
import {PhoneTextField} from "../../../../@bizkeytech";
import {TextField} from "@mui/material";

export type FieldTextFieldInputTypes = "text" | "number" | "phone";

export type FieldTextFieldValue<InputType> =
    InputType extends "number"
        ? number | undefined
        : InputType extends "phone"
            ? string | undefined
            : InputType extends "text"
                ? string | undefined
                : never;

export interface FieldTextFieldProps<
    InputType extends FieldTextFieldInputTypes | undefined,
> {
    inputType?: InputType;
    multiline?: InputType extends 'text' ? boolean : never;
    minRows?: InputType extends 'text' ? number : never;
    maxRows?: InputType extends 'text' ? number : never;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    autoCommit?: boolean;
}

const FieldTextField = <
    InputType extends FieldTextFieldInputTypes = 'text',
>(props: FieldTextFieldProps<InputType>) => {
    const fieldControl = useFieldControl<FieldTextFieldValue<InputType>>();
    const {addValidations, clearValidations, saveChanges} = fieldControl;

    /**
     * Adds the validations for the phone input type.
     */
    useEffect(() => {
        if (props.inputType === "phone") {
            addValidations((value) => (value?.toString() ?? "").length !== 12 ? "Please enter a valid phone number." : undefined)
        } else {
            clearValidations();
        }
    }, [addValidations, clearValidations, props.inputType]);

    /**
     * Handles the key up event.
     * - If the user presses enter, the changes are saved immediately.
     */
    const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case "Enter":
                e.target.dispatchEvent(new Event('blur'));
                setTimeout(() => void saveChanges(), 100)
                break;
        }
    }, [saveChanges])

    return (
        <>
            {
                props.inputType === "phone" &&
                <PhoneTextField<"Mui">
                    form={false}
                    fullWidth
                    variant={'outlined'}
                    value={fieldControl.value ?? ""}
                    onChange={(e) => fieldControl.setValue(e.target.value as any)}
                    onBlur={async (e) => {
                        fieldControl.setValue((!e.target.value ? undefined : e.target.value) as any);
                        if (props.autoCommit ?? true) {
                            await saveChanges();
                        }
                    }}
                    onKeyUp={onKeyUp}
                    disabled={fieldControl.disabled}
                    helperText={fieldControl.helperText}
                    error={fieldControl.erroneous}
                    sx={fieldControl.sx}
                    InputProps={{
                        readOnly: fieldControl.readonly,
                        startAdornment: props.startAdornment,
                        endAdornment: props.endAdornment,
                    }}
                />
            }
            {
                props.inputType === "number" &&
                <TextField
                    fullWidth
                    variant={'outlined'}
                    value={fieldControl.value ?? ""}
                    inputMode={'numeric'}
                    type={'number'}
                    onChange={(e) => fieldControl.setValue(e.target.value as any)}
                    onBlur={async (e) => {
                        fieldControl.setValue((
                            Number.isNaN(parseFloat(e.target.value))
                                ? Number.isNaN(parseFloat(fieldControl.initialValue?.toString() ?? "")) ? fieldControl.initialValue : undefined
                                : Number(e.target.value)
                        ) as any);
                        if (props.autoCommit ?? true) {
                            await saveChanges();
                        }
                    }}
                    onKeyUp={onKeyUp}
                    disabled={fieldControl.disabled}
                    helperText={fieldControl.helperText}
                    error={fieldControl.erroneous}
                    sx={fieldControl.sx}
                    InputProps={{
                        readOnly: fieldControl.readonly,
                        startAdornment: props.startAdornment,
                        endAdornment: props.endAdornment,
                    }}
                />
            }
            {
                (!props.inputType || props.inputType === "text") &&
                <TextField
                    fullWidth
                    variant={'outlined'}
                    value={fieldControl.value ?? ""}
                    onChange={(e) => fieldControl.setValue(e.target.value as any)}
                    onBlur={async (e) => {
                        fieldControl.setValue((!e.target.value ? undefined : e.target.value) as any);
                        if (props.autoCommit ?? true) {
                            await saveChanges();
                        }
                    }}
                    onKeyUp={onKeyUp}
                    disabled={fieldControl.disabled}
                    helperText={fieldControl.helperText}
                    error={fieldControl.erroneous}
                    sx={fieldControl.sx}
                    InputProps={{
                        readOnly: fieldControl.readonly,
                        startAdornment: props.startAdornment,
                        endAdornment: props.endAdornment,
                    }}
                    multiline={props.multiline}
                    minRows={props.minRows}
                    maxRows={props.maxRows}
                />
            }
        </>
    );
}

export default FieldTextField;
